/**
 * G08 — Audio Buses (Master / Music / SFX).
 *
 * Godot-style 3-bus mixer that sits in front of the existing
 * MusicPlayer / BgmPlayer / SoundPlayer implementations. The bus
 * doesn't replace those — it composes the three bus volumes
 * (`effective = master × bus`) and feeds the result to whichever
 * underlying player a call site already uses.
 *
 * Goals (per docs/GAME_TECHNIQUES_WIKI.md G08):
 *   • One master knob to drop/raise everything in unison.
 *   • Music / SFX independently mixable for the kid.
 *   • duck() — temporarily lowers a bus (e.g. music) while a louder
 *     cue (boss-warning SFX, voice line) plays, then restores it.
 *
 * Back-compat contract:
 *   • Existing call sites that imported `SoundPlayer` / `BgmPlayer`
 *     directly KEEP working — they still receive volume updates via
 *     the existing `ef:audio-volume` CustomEvent (now carries an
 *     extra `master` field; old handlers read `.bgm`/`.sfx` only,
 *     which we set to the effective post-master values for them).
 *   • PlayerProgress.audio.bgmVolume / sfxVolume retain the original
 *     "what the user sees on the slider" semantic — we serialize the
 *     slider value (0..1) NOT the post-master multiplied value, so a
 *     kid who turned master to 0.5 then bumped music to 0.9 still
 *     sees 90 on the music slider next session.
 *   • masterVolume is stored on `audio.masterVolume` (optional —
 *     defaults to 1.0 when missing, so v17 saves load cleanly).
 *
 * Design intentionally NON-singleton at the module level (export a
 * default singleton AND the class) so tests can construct isolated
 * instances without sharing state across files.
 */

import {
  loadProgress,
  saveProgress,
  createFreshProgress,
} from './progress';

export type BusName = 'master' | 'music' | 'sfx';

/** Per-bus volume, all in [0, 1]. */
export interface BusVolumes {
  master: number;
  music: number;
  sfx: number;
}

/** Defaults match the pre-G08 settings UI initial values. */
export const DEFAULT_VOLUMES: BusVolumes = {
  master: 1.0,
  music: 0.7,
  sfx: 0.8,
};

/**
 * Underlying audio player adapters. Provided as a constructor option
 * so tests can inject lightweight mocks instead of pulling in
 * BgmPlayer (which spawns AudioContext at construct time).
 *
 * Defaults are wired lazily inside getDefaultAudioBus() so unit tests
 * that only construct AudioBuses directly never touch the DOM.
 */
export interface PlayerAdapter {
  /** Apply the effective bus volume (already master × music). */
  setMusicVolume(v: number): void;
  /** Apply the effective bus volume (already master × sfx). */
  setSfxVolume(v: number): void;
  /** Trigger the music key (e.g. 'battle' / 'boss' / 'academy'). */
  playMusic(key: string, opts?: PlayMusicOpts): void;
  /** Fire a one-shot SFX with the supplied per-shot gain (already master × sfx). */
  playSfx(key: string, gain: number, opts?: PlaySfxOpts): void;
}

export interface PlayMusicOpts {
  /** When true, restart even if `key` is already playing. */
  restart?: boolean;
}

export interface PlaySfxOpts {
  /** Per-shot gain multiplier (0..1). Defaults to 1. */
  gain?: number;
}

/**
 * Linear-ramp scheduler. window.requestAnimationFrame in browsers,
 * setTimeout(0) in test environments / SSR. Returns a cancel fn.
 */
type Ticker = (cb: (nowMs: number) => void) => () => void;

function defaultTicker(): Ticker {
  // Prefer rAF when available — keeps duck tweens 60fps.
  if (typeof globalThis !== 'undefined' &&
      typeof (globalThis as unknown as { requestAnimationFrame?: (cb: FrameRequestCallback) => number }).requestAnimationFrame === 'function') {
    const raf = (globalThis as unknown as { requestAnimationFrame: (cb: FrameRequestCallback) => number }).requestAnimationFrame;
    const caf = (globalThis as unknown as { cancelAnimationFrame: (id: number) => void }).cancelAnimationFrame;
    return (cb) => {
      let id = 0;
      let cancelled = false;
      const loop = (t: number): void => {
        if (cancelled) return;
        cb(t);
        id = raf(loop);
      };
      id = raf(loop);
      return () => { cancelled = true; if (caf) caf(id); };
    };
  }
  // Fallback for happy-dom / Node test envs.
  return (cb) => {
    let cancelled = false;
    const start = Date.now();
    const loop = (): void => {
      if (cancelled) return;
      cb(Date.now() - start);
      setTimeout(loop, 16);
    };
    setTimeout(loop, 16);
    return () => { cancelled = true; };
  };
}

export interface AudioBusesOptions {
  /** Initial volume values; overrides DEFAULT_VOLUMES per-key. */
  initial?: Partial<BusVolumes>;
  /** Player adapter — tests pass a mock; production wiring lives in `installDefaultAdapter`. */
  adapter?: PlayerAdapter;
  /**
   * Custom ticker (mainly for tests).  Default = rAF in browsers,
   * setTimeout in headless envs.
   */
  ticker?: Ticker;
  /**
   * When true, persist volume changes to PlayerProgress + dispatch
   * `ef:audio-volume`. Disabled by default so unit tests don't touch
   * localStorage; production singleton (getDefaultAudioBus) sets true.
   */
  persist?: boolean;
}

/**
 * Internal ducking state per bus. `original` is the pre-duck value
 * so we can ramp back exactly to it (avoiding cumulative drift if
 * duck() is called twice while ducked).
 */
interface DuckState {
  original: number;
  cancel: () => void;
}

/**
 * Snapshot type emitted on every volume change. Mirrors the
 * `ef:audio-volume` CustomEvent payload so listeners can subscribe
 * either to the event OR via the on() method below.
 */
export interface VolumeSnapshot {
  master: number;
  music: number;
  sfx: number;
  /** Effective post-master volumes (master × bus). Convenience for legacy adapters. */
  effectiveMusic: number;
  effectiveSfx: number;
}

export class AudioBuses {
  private vols: BusVolumes;
  private adapter: PlayerAdapter | null;
  private ticker: Ticker;
  private persistEnabled: boolean;
  private ducks: Partial<Record<BusName, DuckState>> = {};
  private listeners = new Set<(snap: VolumeSnapshot) => void>();

  constructor(opts: AudioBusesOptions = {}) {
    this.vols = {
      master: clamp01(opts.initial?.master ?? DEFAULT_VOLUMES.master),
      music:  clamp01(opts.initial?.music  ?? DEFAULT_VOLUMES.music),
      sfx:    clamp01(opts.initial?.sfx    ?? DEFAULT_VOLUMES.sfx),
    };
    this.adapter = opts.adapter ?? null;
    this.ticker = opts.ticker ?? defaultTicker();
    this.persistEnabled = opts.persist === true;
  }

  /** Replace the player adapter after construction (used by lazy install). */
  setAdapter(a: PlayerAdapter | null): void {
    this.adapter = a;
    // Re-push current effective volumes so the brand-new adapter is in sync.
    if (a) {
      a.setMusicVolume(this.effective('music'));
      a.setSfxVolume(this.effective('sfx'));
    }
  }

  /** Return the raw slider value for a bus (pre-master). */
  getBusVolume(bus: BusName): number {
    return this.vols[bus];
  }

  /** All raw slider values. */
  getVolumes(): BusVolumes {
    return { ...this.vols };
  }

  /**
   * Effective post-master volume for `bus`. For 'master' this is
   * just the master value itself; for 'music'/'sfx' it's
   * master × bus, clamped to [0, 1].
   */
  effective(bus: BusName): number {
    if (bus === 'master') return this.vols.master;
    return clamp01(this.vols.master * this.vols[bus]);
  }

  /**
   * Update a single bus.  Recomputes effective volumes, pushes to the
   * adapter, persists (if enabled) and notifies listeners.
   */
  setBusVolume(bus: BusName, v: number): void {
    const clamped = clamp01(v);
    if (this.vols[bus] === clamped) return; // noop — avoid event spam
    this.vols[bus] = clamped;
    this.pushToAdapter();
    this.notify();
    if (this.persistEnabled) {
      this.persist();
      this.dispatch();
    }
  }

  /**
   * Convenience: set music + sfx + master in one call.  Skips missing
   * keys.  Same persistence/notification semantics as setBusVolume.
   */
  setAll(partial: Partial<BusVolumes>): void {
    let changed = false;
    for (const key of ['master', 'music', 'sfx'] as const) {
      const next = partial[key];
      if (next === undefined) continue;
      const c = clamp01(next);
      if (this.vols[key] !== c) { this.vols[key] = c; changed = true; }
    }
    if (!changed) return;
    this.pushToAdapter();
    this.notify();
    if (this.persistEnabled) {
      this.persist();
      this.dispatch();
    }
  }

  /**
   * Play (or restart) a music track via the adapter. No-op if no
   * adapter is wired (tests that construct AudioBuses without one).
   */
  playMusic(key: string, opts?: PlayMusicOpts): void {
    if (!this.adapter) return;
    this.adapter.playMusic(key, opts);
    // Re-push volume in case the adapter recreates internal nodes on play.
    this.adapter.setMusicVolume(this.effective('music'));
  }

  /**
   * Fire a one-shot SFX. `opts.gain` is the per-shot multiplier
   * (e.g. 0.45 for the boss-warning rumble) — we multiply by the
   * effective bus volume so the kid's master/sfx sliders still apply.
   */
  playSfx(key: string, opts: PlaySfxOpts = {}): void {
    if (!this.adapter) return;
    const perShot = clamp01(opts.gain ?? 1);
    const composed = clamp01(this.effective('sfx') * perShot);
    this.adapter.playSfx(key, composed, opts);
  }

  /**
   * Temporarily ramp `bus` from its current value down to `toVolume`,
   * hold for the ducked window, then ramp back to the pre-duck value.
   *
   * Total wall-clock = durationMs (split 25% ramp-down / 50% hold /
   * 25% ramp-up). This matches the Godot ducking convention used by
   * dialog/voice tracks — short fades feel natural, instant cuts pop.
   *
   * Calling duck() on a bus that's already ducked cancels the in-
   * flight tween and restarts from CURRENT value but restores to the
   * ORIGINAL pre-first-duck value, so chained boss cues don't leave
   * the bus permanently lowered.
   */
  duck(bus: BusName, toVolume: number, durationMs: number): void {
    const target = clamp01(toVolume);
    const dur = Math.max(50, durationMs);
    const existing = this.ducks[bus];
    // Preserve the FIRST duck's original value across overlapping calls.
    const original = existing ? existing.original : this.vols[bus];
    existing?.cancel();

    const fadeMs = dur * 0.25;
    const holdMs = dur * 0.50;
    const startVal = this.vols[bus];

    let cancelFn: (() => void) | null = null;
    cancelFn = this.ticker((elapsed) => {
      let next: number;
      if (elapsed < fadeMs) {
        // Ramp down
        const t = elapsed / fadeMs;
        next = startVal + (target - startVal) * t;
      } else if (elapsed < fadeMs + holdMs) {
        next = target;
      } else if (elapsed < dur) {
        // Ramp back up
        const t = (elapsed - fadeMs - holdMs) / fadeMs;
        next = target + (original - target) * t;
      } else {
        // Done — restore exact original (avoid float drift) + clean state.
        // Stop our own ticker first so it doesn't fire again after we
        // delete the duck-state record.
        cancelFn?.();
        this.vols[bus] = original;
        this.pushToAdapter();
        this.notify();
        delete this.ducks[bus];
        return;
      }
      this.vols[bus] = clamp01(next);
      this.pushToAdapter();
      this.notify();
    });

    this.ducks[bus] = { original, cancel: cancelFn };
  }

  /** True iff `bus` is currently in a duck tween. */
  isDucking(bus: BusName): boolean {
    return this.ducks[bus] !== undefined;
  }

  /**
   * Subscribe to volume changes (including in-flight duck ticks).
   * Returns an unsubscribe fn.
   */
  on(listener: (snap: VolumeSnapshot) => void): () => void {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  }

  /** Force-stop any active duck tween and restore originals. Used by tests + scene teardown. */
  cancelAllDucks(): void {
    for (const bus of ['master', 'music', 'sfx'] as const) {
      const d = this.ducks[bus];
      if (!d) continue;
      d.cancel();
      this.vols[bus] = d.original;
      delete this.ducks[bus];
    }
    this.pushToAdapter();
    this.notify();
  }

  /**
   * Compute & push effective volumes to the adapter. Safe to call
   * with no adapter — early-return makes this a no-op.
   */
  private pushToAdapter(): void {
    if (!this.adapter) return;
    this.adapter.setMusicVolume(this.effective('music'));
    this.adapter.setSfxVolume(this.effective('sfx'));
  }

  private snapshot(): VolumeSnapshot {
    return {
      master: this.vols.master,
      music:  this.vols.music,
      sfx:    this.vols.sfx,
      effectiveMusic: this.effective('music'),
      effectiveSfx:   this.effective('sfx'),
    };
  }

  private notify(): void {
    const snap = this.snapshot();
    for (const fn of this.listeners) {
      try { fn(snap); } catch { /* listener bug must not break audio */ }
    }
  }

  /**
   * Dispatch the legacy `ef:audio-volume` CustomEvent. Detail carries
   * BOTH the raw slider values AND the post-master effective values
   * so existing consumers (settings.ts → setLearnMusicVolume, etc.)
   * can pick the field they care about.
   */
  private dispatch(): void {
    if (typeof window === 'undefined' || !window.dispatchEvent) return;
    const snap = this.snapshot();
    try {
      window.dispatchEvent(new CustomEvent('ef:audio-volume', {
        detail: {
          // Legacy fields read by ui-sounds.ts / learn-music.ts /
          // horizontal-battle-scene.ts — these are the POST-master
          // values so a master-knob drag still affects them without
          // those modules knowing about the master bus.
          bgm: snap.effectiveMusic,
          sfx: snap.effectiveSfx,
          // New G08 fields. Optional, gated by feature detection.
          master: snap.master,
          music: snap.music,
          sfxRaw: snap.sfx,
        },
      }));
    } catch { /* SSR / test envs without CustomEvent — best effort */ }
  }

  /** Persist current volumes into PlayerProgress.audio. */
  private persist(): void {
    try {
      const p = loadProgress() ?? createFreshProgress('指揮官');
      if (!p.audio) {
        p.audio = { bgmVolume: 0.7, sfxVolume: 0.8 };
      }
      p.audio.bgmVolume = this.vols.music;
      p.audio.sfxVolume = this.vols.sfx;
      // Optional master — write through type assertion since the v17
      // PlayerProgress type doesn't yet declare it (back-compat: older
      // installs load fine, missing field defaults to 1.0 in load()).
      (p.audio as { masterVolume?: number }).masterVolume = this.vols.master;
      saveProgress(p);
    } catch { /* localStorage may be unavailable in tests/SSR */ }
  }
}

function clamp01(v: number): number {
  if (!Number.isFinite(v)) return 0;
  return Math.max(0, Math.min(1, v));
}

// ── Default singleton wired to the real BgmPlayer / SoundPlayer ───────────────
//
// Lazily instantiated so unit tests for `AudioBuses` (which import
// only the class) don't pull in BgmPlayer (which spawns AudioContext
// at construct time, blowing up in environments without WebAudio).

let _defaultBus: AudioBuses | null = null;

/**
 * Read the persisted volumes from PlayerProgress (if any) and
 * construct the singleton bus. Subsequent calls return the same
 * instance.  Adapter is wired on first call; safe to call from
 * top-level module load.
 */
export function getDefaultAudioBus(): AudioBuses {
  if (_defaultBus) return _defaultBus;
  const initial = loadInitialVolumes();
  _defaultBus = new AudioBuses({ initial, persist: true });
  return _defaultBus;
}

/**
 * Install the production player adapter (BgmPlayer + SoundPlayer).
 * Separated from getDefaultAudioBus() so the wiring code only runs in
 * the learn / game entry points — pure-logic tests can use
 * getDefaultAudioBus() without the WebAudio overhead.
 */
export function installDefaultAdapter(adapter: PlayerAdapter): AudioBuses {
  const bus = getDefaultAudioBus();
  bus.setAdapter(adapter);
  return bus;
}

/**
 * Read PlayerProgress.audio + apply defaults for missing fields
 * (notably masterVolume, which is new in G08).
 */
function loadInitialVolumes(): BusVolumes {
  try {
    const p = loadProgress();
    if (!p?.audio) return { ...DEFAULT_VOLUMES };
    const raw = p.audio as { bgmVolume: number; sfxVolume: number; masterVolume?: number };
    return {
      master: clamp01(raw.masterVolume ?? DEFAULT_VOLUMES.master),
      music:  clamp01(raw.bgmVolume    ?? DEFAULT_VOLUMES.music),
      sfx:    clamp01(raw.sfxVolume    ?? DEFAULT_VOLUMES.sfx),
    };
  } catch {
    return { ...DEFAULT_VOLUMES };
  }
}

/** Test-only: reset the singleton so each suite starts clean. */
export function _resetDefaultAudioBusForTests(): void {
  _defaultBus = null;
}
